import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useRecords, Empty, Err, ago } from '../ui.js';
import { sweepStatus, runSweep, createRecord, ApiError } from '../api.js';
import type { SweepSource, SweepStatus, Signal } from '../api.js';

// "Collect now" — the manual half of opportunity collection.
//
// Runs the same handler the daily agent does, over a window you pick. The point
// of having it in the app rather than only on a schedule is that a collector
// which silently stopped looks exactly like a quiet week, and those two need
// different responses.

const WINDOWS = [
  { days: 1, label: 'Today' },
  { days: 7, label: '7 days' },
  { days: 30, label: '30 days' },
  { days: 90, label: '90 days' },
];

export function Sweeper() {
  const [status, setStatus] = useState<SweepStatus | null>(null);
  const [days, setDays] = useState(7);
  const [busy, setBusy] = useState(false);
  const [msg, setMsg] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const signals = useRecords<Signal & { id: string }>('signals', { status: 'new' });
  const nav = useNavigate();

  useEffect(() => { sweepStatus().then(setStatus).catch(() => {}); }, []);

  const live = status?.sources.filter((s) => s.available) ?? [];
  const blocked = status?.sources.filter((s) => !s.available) ?? [];
  const last = status?.last ?? null;
  const stale = last
    ? (Date.now() - new Date(last.created_at).getTime()) / 86_400_000 > 2
    : true;

  async function collect() {
    setBusy(true); setError(null); setMsg(null);
    try {
      const r = await runSweep({ days });
      setMsg(`${r.note} Scanned ${r.scanned} message(s) from ${r.ran.join(', ') || 'nothing'}.`);
      void signals.reload();
      sweepStatus().then(setStatus).catch(() => {});
    } catch (e) {
      setError(e instanceof ApiError ? e.message : String(e));
    } finally { setBusy(false); }
  }

  return (
    <section className="panel">
      <h4>Opportunity sweep</h4>

      {/* A collector that stopped and a quiet week look identical without this. */}
      <p className={stale ? 'warnbox' : 'meta'}>
        {last
          ? `Last collection ${ago(last.created_at)} — ${last.found} found from ${last.scanned} scanned (${last.trigger}).`
          : 'Never collected. Nothing has scanned for opportunities yet.'}
      </p>

      <div className="toolbar">
        <div className="toggle">
          {WINDOWS.map((w) => (
            <button key={w.days} className={days === w.days ? 'on' : ''}
              onClick={() => setDays(w.days)}>{w.label}</button>
          ))}
        </div>
        <button className="primary" disabled={busy || live.length === 0}
          title={live.length ? '' : 'No collectors are configured'}
          onClick={() => void collect()}>
          {busy ? 'Collecting…' : 'Collect now'}
        </button>
        <span className="meta">
          {live.length
            ? `${live.map((s) => s.label).join(', ')}`
            : 'no sources available'}
        </span>
      </div>

      <Err>{error}</Err>
      {msg && <p className="ok-note">{msg}</p>}

      {blocked.length > 0 && (
        <div className="blockedsrc">
          {blocked.map((s: SweepSource) => (
            <p className="meta" key={s.key}>
              <strong>{s.label}</strong> — {s.blocked_by} {s.fix}
            </p>
          ))}
        </div>
      )}

      {signals.rows?.length === 0 && (
        <Empty title="Nothing waiting"
          hint="Signals a collector found land here for you to promote or dismiss. Nothing becomes a deal on its own." />
      )}

      {signals.rows?.map((s) => (
        <div className="event" key={s.id}>
          <div className="row">
            <span className="rname">{s.subject ?? s.excerpt?.slice(0, 60) ?? '(no subject)'}</span>
            {s.score !== null && (
              <span className={`flag ${s.score >= 80 ? 'flag-ok' : s.score >= 50 ? 'flag-warn' : 'flag-mute'}`}>
                {s.score}
              </span>
            )}
            {s.intent && <span className="flag flag-mute">{s.intent.replace('_', ' ')}</span>}
            <span className="meta">{s.from_handle ?? s.from_name ?? s.source}</span>
            <span className="meta">{s.occurred_at ? ago(s.occurred_at) : ''}</span>
            <span className="flags">
              <button className="mini" onClick={async () => {
                setError(null);
                try {
                  // Promote into a deal on the matched account. Without a match
                  // there is nowhere to hang it, so send Kyle to research instead
                  // of inventing a company from an email domain.
                  if (!s.company_id) { nav('/research'); return; }
                  const d = await createRecord<{ id: string }>('deals', {
                    company_id: s.company_id,
                    name: s.subject?.slice(0, 120) || `Inbound — ${s.from_handle ?? s.source}`,
                    stage: 'scoping', source: `Sweep · ${s.source}`,
                    notes: `${s.reasoning ?? ''}\n\n${s.excerpt ?? ''}`.trim(),
                  });
                  await signals.update(s.id, { status: 'promoted', promoted_deal_id: d.record.id });
                  nav('/deals');
                } catch (e) {
                  setError(e instanceof ApiError ? e.message : String(e));
                }
              }}>{s.company_id ? 'Open a deal' : 'No account — research'}</button>
              <button className="mini" onClick={() => void signals.update(s.id, {
                status: 'dismissed', dismissed_reason: 'Not an opportunity',
              })}>Dismiss</button>
            </span>
          </div>
          {s.reasoning && <p className="meta">{s.reasoning}</p>}
          {s.excerpt && <p className="meta" style={{ whiteSpace: 'pre-wrap' }}>
            {s.excerpt.slice(0, 300)}
          </p>}
        </div>
      ))}
    </section>
  );
}
